import { z } from 'zod';
/**
 * Public Communities REST DTO — Phase 18 / Plan 18-01 (D18-CONTRACT-01).
 *
 * 엔드포인트 (공개 — SessionMiddleware `.exclude('api/v1/public/*splat')`):
 *   - `GET /api/v1/public/communities` — `/communities` 디렉터리 + 메인 6장
 *     CommunityStrip 이 ISR/SWR 로 조회하는 단일 payload.
 *
 * 핵심 불변 (CONTEXT.md D-23 / T-18-INFO-DISCLOSURE):
 *   - 응답 필드는 공개 한정. **email · phone · kyc_* · tossSellerStatus 절대 미포함**.
 *   - `streamers.id · channelSlug · displayName · bio` + `users.image (avatarUrl)` + 공개 집계만.
 *   - **LIVE 상태 필드는 절대 포함되지 않는다** — `isLive` · `viewerCount` 키는 스키마에
 *     정의조차 하지 않는다 (CLAUDE.md Pitfall 2 / public-streamer.rest.ts 동일 정책 답습).
 *   - `subscriberCount` 는 **NON-NULL contract** — 후속 plan (18-04 디렉터리 ·
 *     18-05 메인 CommunityStrip) 이 `number` 타입을 기대. service 레이어가
 *     `COALESCE(..., 0)` 으로 0 을 채워 항상 number 를 반환할 책임을 진다.
 *   - 카드 array 길이 max 48 — D-12 페이지 크기 24 + 안전 buffer.
 *   - 정렬 키 3종 (`recent` · `subs` · `new`) — D-07.
 *   - cursor max 128 chars — `{streamerId}_{value}` 구조 (D-11).
 *
 * 위협 모델 (18-PLAN §threat_model 답습):
 *   - T-18-INFO-DISCLOSURE (I): strict whitelist + `.strict()` parse → 서버가 실수로
 *     PII 컬럼을 직렬화해도 zod parse 가 reject 한다. `subscriberCount: nullable` 도
 *     의도적 거부 — 후속 plan 들의 number 타입 가정과 drift 방지.
 *   - T-18-TAMPERING (T): query schema 가 `sort` enum, `limit` range, `q` 길이를
 *     서버 boundary 에서 강제 — Route Handler 는 forwarded querystring 만 전달.
 *
 * 패턴 출처:
 *   - `public-landing.rest.ts` (liveCardSchema · response wrapper).
 *   - `public-streamer.rest.ts` (`.strict()` 모드 · LIVE 필드 부재 가드).
 */
/**
 * Phase 18 — 디렉터리 카드 1장.
 *
 * 공개 한정 필드 — D-23 화이트리스트.
 * **LIVE 상태 미포함** — Pitfall 2 (`isLive`/`viewerCount` 키 정의 자체 금지).
 * 메인 6장에서 LIVE 배지가 필요하면 클라이언트 SSE 별도 구독.
 */
export const communityCardSchema = z
    .object({
    streamerId: z.string().uuid(),
    channelSlug: z.string().min(1).max(64),
    displayName: z.string(),
    bio: z.string().nullable(),
    avatarUrl: z.string().url().nullable(),
    /**
     * D-06 — 최근 24h 내 shadow-ban 제외 published 게시글 수.
     * service 가 `COUNT(*) FILTER (WHERE published_at >= now() - interval '24 hours'
     *   AND is_shadowed = false AND deleted_at IS NULL)` 로 계산.
     */
    recentPostCount24h: z.number().int().nonnegative(),
    /**
     * D-07 sort=subs 정렬 기준 — 활성 구독자 수.
     * **NON-NULL contract** — service 가 0 을 폴백으로 채워야 한다.
     * 후속 plan (18-04 디렉터리 · 18-05 메인 strip) 이 `number` 타입을 기대.
     */
    subscriberCount: z.number().int().nonnegative(),
    /** D-07 sort=new — 스트리머 가입 시각 (ISO 8601 with offset). */
    streamerCreatedAt: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * 정렬 키 — D-07. 디렉터리 querystring `?sort=recent|subs|new`.
 * 메인 strip 은 항상 `recent` (D-08, 정렬 토글 없음).
 */
export const sortKeySchema = z.enum(['recent', 'subs', 'new']);
/**
 * GET /api/v1/public/communities querystring 계약.
 * Controller 가 zod safeParse 로 검증 — invalid 면 400 BadRequest.
 */
export const publicCommunitiesQuerySchema = z.object({
    /** D-07 — 기본값 `recent` (D-06). */
    sort: sortKeySchema.default('recent'),
    /** D-09/10 — ILIKE prefix 검색어. 64자 이내. */
    q: z.string().max(64).optional(),
    /** D-11 — `{streamerId}_{value}` 구조. base64/JSON 금지. */
    cursor: z.string().max(128).optional(),
    /** D-12 — 페이지 크기. 기본 24, 최대 48 안전. */
    limit: z.number().int().min(1).max(48).default(24),
});
/**
 * GET /api/v1/public/communities 응답.
 *
 * Next.js Route Handler `apps/web/src/app/api/public/communities/route.ts` (Plan 18-03)
 * 가 이 shape 을 ISR 30s 로 캐시해 페이지 RSC + CommunityStrip 클라이언트 컴포넌트에
 * 전달.
 */
export const publicCommunitiesResponseSchema = z
    .object({
    /** 카드 array — 최대 48 (D-12 24 + 안전 buffer). */
    items: z.array(communityCardSchema).max(48),
    /** 다음 페이지 cursor — 마지막 페이지 또는 빈 결과면 null. */
    nextCursor: z.string().nullable(),
    /** 서버 snapshot 시각 — ISR hit 여부 관측 참고. */
    fetchedAt: z.string().datetime({ offset: true }),
})
    .strict();
/**
 * Phase 18 페이지 크기 상수.
 *   - `COMMUNITY_STRIP_SIZE` — 메인 `/` CommunityStrip 카드 수 (D-04). 항상 6.
 *   - `COMMUNITY_DIRECTORY_PAGE_SIZE` — `/communities` 페이지 1페이지 카드 수 (D-12). 24.
 *
 * apps/api 서비스 (Plan 18-02) 와 apps/web 페이지 (Plan 18-04 / 18-05) 가 공유하므로
 * 단일 출처는 contracts 패키지가 권위. limit query 의 max 48 은 24 + 안전 buffer.
 */
export const COMMUNITY_STRIP_SIZE = 6;
export const COMMUNITY_DIRECTORY_PAGE_SIZE = 24;
//# sourceMappingURL=public-communities.rest.js.map